import { useState, useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

const AdminRoute = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const { toast } = useToast();
  const location = useLocation();
  
  useEffect(() => {
    let mounted = true;
    
    const checkAuth = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Error checking authentication:", error);
          if (mounted) setIsAuthenticated(false);
          return;
        }
        
        if (data.session) {
          const { data: userData, error: userError } = await supabase
            .from('admin_users')
            .select('*')
            .eq('email', data.session.user.email)
            .single();
          
          if (userError || !userData) {
            if (mounted) setIsAuthenticated(false);
            await supabase.auth.signOut();
            return;
          }
          
          if (mounted) setIsAuthenticated(true);
        } else {
          if (mounted) setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("Authentication check failed:", error);
        if (mounted) setIsAuthenticated(false);
      }
    };
    
    checkAuth();
    
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN') {
        const { data: userData, error: userError } = await supabase
          .from('admin_users')
          .select('*')
          .eq('email', session?.user.email)
          .single();
          
        if (userError || !userData) {
          if (mounted) setIsAuthenticated(false);
          await supabase.auth.signOut();
          return;
        }
        
        if (mounted) setIsAuthenticated(true);
      } else if (event === 'SIGNED_OUT') {
        if (mounted) setIsAuthenticated(false);
      }
    });
    
    return () => {
      mounted = false;
      authListener.subscription.unsubscribe();
    };
  }, [toast]);
  
  if (isAuthenticated === null) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Verifying authentication...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }
  
  return <Outlet />;
};

export default AdminRoute;
